import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Camera, ChevronRight, ImageOff, Loader2, Sparkles } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { photoThumb, photoThumbBackend } from "@/lib/photoUrl";
import { uploadInChunks } from "@/lib/chunkedUpload";
import { maybeCompressImage } from "@/lib/compressImage";
import { useLanguage } from "@/context/LanguageContext";
import { toast } from "@/components/ui/sonner";
import { Checkbox } from "@/components/ui/checkbox";
import { isNewProduct } from "@/lib/productBadges";
import { FavoriteStar } from "@/components/products/FavoriteStar";

// App-style card list for phones: photo · item number · description · price,
// with a camera quick-action to shoot & upload the product photo on site.
// 2026-10-12 · Added batch-selection checkbox so the same bulk ops as desktop
// (加入报价单 / 打标签 / 删除 / Grid PDF / 复制 ...) work on phones too.
export const MobileProductsList = ({ products, loading, currency, canUpload, onPhotoUpdated,
                                     selected = null, onToggleSelect = null, canSelect = false }) => {
  const navigate = useNavigate();
  const { t } = useLanguage();
  const priceKey = currency === "CAD" ? "display_price_cad" : "display_price_usd";
  const sym = currency === "CAD" ? "C$" : "US$";
  const [uploadingItem, setUploadingItem] = useState(null);
  // Same R2-404 fallback used by <ProductThumb> — swap to the backend URL so a
  // missing thumbnail is generated on demand and re-cached to R2.
  const swapToBackend = (url, size) => (e) => {
    const img = e.currentTarget;
    if (img.dataset.fallbackTried === "1") return;
    const backendUrl = photoThumbBackend(url, size);
    if (backendUrl && backendUrl !== img.src) {
      img.dataset.fallbackTried = "1";
      img.src = backendUrl;
    }
  };

  const handleCapture = async (p, file) => {
    if (!file) return;
    setUploadingItem(p.item_number);
    try {
      const compressed = await maybeCompressImage(file);
      const uploadId = await uploadInChunks(compressed);
      const fd = new FormData();
      fd.append("upload_id", uploadId);
      fd.append("filename", compressed.name);
      const { data } = await api.post("/products/upload-image", fd);
      await api.put(`/products/${encodeURIComponent(p.item_number)}`, { photo_url: data.url });
      onPhotoUpdated(p.item_number, data.url);
      toast.success(t("products.photoUpdated"));
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setUploadingItem(null);
    }
  };

  const openProduct = (p) => navigate(`/products/${encodeURIComponent(p.item_number)}/edit`);

  if (!loading && products.length === 0) {
    return <p className="text-center text-sm text-muted-foreground py-12" data-testid="mobile-products-empty">{t("products.noProducts")}</p>;
  }

  return (
    <div className="space-y-2.5" data-testid="mobile-products-list">
      {products.map((p) => {
        const priceRow = (p.price_lists || []).find((r) => r[priceKey] > 0);
        const firstSize = (p.price_lists || [])[0]?.size;
        const extra = Math.max(0, (p.price_lists || []).length - 1);
        return (
          <div key={p.item_number} className="bg-card border border-border rounded-lg p-3 flex gap-3" data-testid={`mobile-product-card-${p.item_number}`}>
            {/* 2026-10-12 · 批量选择 checkbox — 只在 canSelect && 外部提供 selected/onToggleSelect 时渲染 */}
            {canSelect && selected && onToggleSelect && (
              <div className="shrink-0 flex items-start pt-1" onClick={(e) => e.stopPropagation()}>
                <Checkbox
                  checked={selected.has(p.item_number)}
                  onCheckedChange={() => onToggleSelect(p.item_number)}
                  data-testid={`m-product-select-${p.item_number}`}
                  className="h-5 w-5"
                />
              </div>
            )}
            <button type="button" onClick={() => openProduct(p)} className={`${p.composite_photo_url ? "w-20" : (p.pattern_photo_url && p.photo_url ? "w-32" : "w-20")} h-20 shrink-0 rounded-md border border-border bg-white flex items-center justify-center overflow-hidden`}>
              {p.composite_photo_url ? (
                <img loading="lazy" src={photoThumb(p.composite_photo_url, "sm")} alt={p.item_number} className="w-full h-full object-contain" onError={swapToBackend(p.composite_photo_url, "sm")} />
              ) : p.photo_url || p.pattern_photo_url ? (
                <div className="w-full h-full flex items-stretch gap-0.5">
                  {p.photo_url && (
                    <img loading="lazy" src={photoThumb(p.photo_url, "sm")} alt={p.item_number} className={`${p.pattern_photo_url ? "w-1/2" : "w-full"} h-full object-contain`} onError={swapToBackend(p.photo_url, "sm")} />
                  )}
                  {p.pattern_photo_url && (
                    <img loading="lazy" src={photoThumb(p.pattern_photo_url, "sm")} alt={`${p.item_number} pattern`} className={`${p.photo_url ? "w-1/2" : "w-full"} h-full object-contain`} onError={swapToBackend(p.pattern_photo_url, "sm")} />
                  )}
                </div>
              ) : (
                <ImageOff size={22} className="text-muted-foreground/40" />
              )}
            </button>
            <button type="button" onClick={() => openProduct(p)} className="flex-1 min-w-0 text-left">
              <div className="font-mono text-sm font-bold truncate flex items-center gap-1.5">
                <span onClick={(e) => e.stopPropagation()} className="inline-flex shrink-0">
                  <FavoriteStar itemNumber={p.item_number} initial={p.is_favorite} size={14} />
                </span>
                <span className="truncate">{p.item_number}</span>
                {isNewProduct(p) && (
                  <span
                    className="shrink-0 inline-flex items-center gap-0.5 px-1 py-0.5 rounded-sm bg-amber-400 text-black text-[9px] font-bold uppercase tracking-wide leading-none"
                    data-testid={`mobile-product-new-badge-${p.item_number}`}
                  >
                    <Sparkles size={8} /> {t("products.newBadge")}
                  </span>
                )}
              </div>
              <p className="text-xs text-muted-foreground line-clamp-2 mt-0.5">{p.description || "-"}</p>
              <div className="flex items-baseline gap-2 mt-1">
                {priceRow ? (
                  <span className="font-mono text-sm font-bold text-brand" data-testid={`mobile-price-${p.item_number}`}>
                    {sym}{priceRow[priceKey].toFixed(2)}
                  </span>
                ) : (
                  <span className="text-xs text-muted-foreground">-</span>
                )}
                {firstSize && (
                  <span className="text-[10px] text-muted-foreground font-mono truncate">
                    {firstSize}{extra > 0 ? ` +${extra}` : ""}
                  </span>
                )}
              </div>
            </button>
            <div className="flex flex-col items-center justify-between shrink-0">
              {canUpload && (
                <label className="w-9 h-9 rounded-md border border-border flex items-center justify-center active:bg-secondary cursor-pointer" data-testid={`mobile-photo-btn-${p.item_number}`}>
                  <input
                    type="file"
                    accept="image/*"
                    capture="environment"
                    className="hidden"
                    onChange={(e) => { handleCapture(p, e.target.files?.[0]); e.target.value = ""; }}
                  />
                  {uploadingItem === p.item_number ? (
                    <Loader2 size={16} className="animate-spin text-brand" />
                  ) : (
                    <Camera size={16} className="text-brand" />
                  )}
                </label>
              )}
              <ChevronRight size={16} className="text-muted-foreground" />
            </div>
          </div>
        );
      })}
    </div>
  );
};
